import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { HistoryPanel } from '../history/HistoryPanel';
import { RowMenu } from '../ui/RowMenu';
import { DaySchemeEditor, type DayScheme } from './DaySchemeEditor';
import { DeleteDialog, PRICING_KEY, PricingNav, SCHEMES_RESOURCE } from './PricingLayout';
import { schemePreviewParts, schemeText } from './units';

export function DaySchemesPage() {
  const { me } = useMe();
  const queryClient = useQueryClient();
  // `undefined`: editor closed; `null`: adding; a scheme: editing it
  const [editing, setEditing] = useState<DayScheme | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<DayScheme | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const list = useQuery({ queryKey: [...PRICING_KEY, 'schemes'], queryFn: () => api<{ schemes: DayScheme[] }>('/api/pricing/day-schemes') });
  const makeDefault = useMutation({
    mutationFn: (id: string) => api<DayScheme>(`/api/pricing/day-schemes/${id}/default`, { method: 'POST' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: PRICING_KEY }),
  });
  if (!me) return null;
  const schemes = list.data?.schemes ?? [];
  const seeHistory = can(me, 'history.entries', 'view');

  return (
    <section className="page" data-feature-id="pricing.day_schemes">
      <div className="page-head">
        <h2>Cenniki</h2>
        {can(me, SCHEMES_RESOURCE, 'create') && editing === undefined && (
          <button className="btn primary" type="button" onClick={() => setEditing(null)}>
            Dodaj schemat
          </button>
        )}
      </div>
      <PricingNav me={me} />
      <p className="muted">Schemat dni mówi, ile kosztuje każdy kolejny dzień wynajmu sprzętu. Domyślny schemat trafia do nowych kosztorysów.</p>

      {editing !== undefined && <DaySchemeEditor key={editing?.id ?? 'new'} {...(editing ? { scheme: editing } : {})} onClose={() => setEditing(undefined)} />}

      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać schematów'}</p>}
      {makeDefault.error && <p className="alert" role="alert">{makeDefault.error instanceof ApiError ? makeDefault.error.message : 'Nie udało się zmienić domyślnego schematu'}</p>}
      {schemes.length > 0 && (
        <div className="table-wrap">
          <table className="table pricing-table" aria-label="Schematy dni">
            <thead>
              <tr>
                <th scope="col">Nazwa</th>
                <th scope="col">Procenty dni</th>
                <th scope="col" className="hide-phone">Podgląd</th>
                <th scope="col" className="tpl-actions-col">
                  <span className="visually-hidden">Działania</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {schemes.map((s) => {
                const editable = !s.system && can(me, SCHEMES_RESOURCE, 'edit');
                const more = [
                  ...(editable && !s.isDefault ? [{ label: 'Ustaw jako domyślny', onSelect: () => makeDefault.mutate(s.id) }] : []),
                  ...(seeHistory ? [{ label: historyId === s.id ? 'Ukryj historię zmian' : 'Historia zmian', onSelect: () => setHistoryId(historyId === s.id ? null : s.id) }] : []),
                  ...(!s.system && !s.isDefault && can(me, SCHEMES_RESOURCE, 'delete') ? [{ label: 'Usuń', danger: true, onSelect: () => setDeleting(s) }] : []),
                ];
                return (
                  <Fragment key={s.id}>
                    <tr>
                      <td>
                        <strong>{s.name}</strong>
                        {s.isDefault && <span className="chip owner">Domyślny</span>}
                        {s.system && <span className="chip">Systemowy</span>}
                        {s.system && <div className="muted hide-phone">Dla usług: każdy dzień 100%.</div>}
                      </td>
                      <td className="num-text">{schemeText(s.pctsBp)}</td>
                      <td className="hide-phone muted preview">
                        {schemePreviewParts(s.pctsBp).map((part, i) => (
                          <span key={part}>
                            {i > 0 && ' · '}
                            {part}
                          </span>
                        ))}
                      </td>
                      <td className="tpl-actions">
                        {editable && (
                          <button className="btn small" type="button" aria-label={`Edytuj: ${s.name}`} onClick={() => setEditing(s)}>
                            Edytuj
                          </button>
                        )}
                        <RowMenu label={`Więcej działań: ${s.name}`} actions={more} />
                      </td>
                    </tr>
                    {historyId === s.id && seeHistory && (
                      <tr>
                        <td colSpan={4}>
                          <HistoryPanel entityId={s.id} resource={SCHEMES_RESOURCE} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {deleting && <DeleteDialog title="Usunąć schemat dni?" name={deleting.name} url={`/api/pricing/day-schemes/${deleting.id}?version=${deleting.version}`} onClose={() => setDeleting(null)} />}
    </section>
  );
}
